// Cart.js (integrado con crear-pedido y confirmación WhatsApp)

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useCart } from '../../context/CartContext';
import baseURL from '../url';
import './Cart.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faShoppingCart,
  faTrash,
  faPlus,
  faMinus,
  faHeart,
  faShoppingBag,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { Link as Anchor } from "react-router-dom";
import moneda from '../moneda';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

// Opcional: si tienes un endpoint de contacto para obtener el teléfono del vendedor
const fetchVendorPhone = async () => {
  try {
    const res = await fetch(`${baseURL}/contactoGet.php`);
    const data = await res.json();
    const contacto = (data?.contacto || []).reverse()[0];
    return contacto?.telefono || ''; // Debe venir sin +57
  } catch {
    return '';
  }
};

export default function Cart() {
  const { cart, removeFromCart, updateQuantity, clearCart, total, totalItems } = useCart();

  // Estados locales
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [checkoutModalIsOpen, setCheckoutModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [removingItems, setRemovingItems] = useState(new Set());
  const [vendorPhone, setVendorPhone] = useState('');

  // Form
  const [formData, setFormData] = useState({
    noteText: '',
    name: '',
    email: '',
    telefono: '',
    identificationNumber: '',     // <- número de identificación
    deliveryOption: 'delivery',   // 'delivery' | 'pickup'
    address: '',
    paymentMethod: 'efectivo'     // 'efectivo' | 'transferencia'
  });

  useEffect(() => {
    (async () => {
      const phone = await fetchVendorPhone();
      setVendorPhone(phone);
    })();
  }, []);

  // Animaciones suaves para abrir/cerrar modales
  const handleOpenCart = () => {
    setModalIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseCart = () => {
    setModalIsOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleOpenCheckout = () => {
    if (cart.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    setModalIsOpen(false);
    setTimeout(() => setCheckoutModalIsOpen(true), 150);
  };

  const handleCloseCheckout = () => {
    setCheckoutModalIsOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Eliminar item con confirmación
  const handleRemoveItem = async (idProductoTalla, titulo) => {
    const result = await Swal.fire({
      title: '¿Eliminar producto?',
      text: `¿Eliminar "${titulo}" del carrito?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      customClass: { container: 'swal-container', popup: 'swal-popup' }
    });
    if (result.isConfirmed) {
      setRemovingItems(prev => new Set([...prev, idProductoTalla]));
      setTimeout(() => {
        removeFromCart(idProductoTalla);
        setRemovingItems(prev => {
          const s = new Set(prev);
          s.delete(idProductoTalla);
          return s;
        });
        toast.success('Producto eliminado del carrito');
      }, 300);
    }
  };

  // Vaciar carrito
  const handleClearCart = async () => {
    if (cart.length === 0) return;
    const result = await Swal.fire({
      title: '¿Vaciar carrito?',
      text: 'Se eliminarán todos los productos del carrito',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, vaciar',
      cancelButtonText: 'Cancelar'
    });
    if (result.isConfirmed) {
      clearCart();
    }
  };

  // Actualizar cantidad
  const handleUpdateQuantity = (idProductoTalla, newQuantity) => {
    if (newQuantity < 1) {
      const item = cart.find(i => i.idProductoTalla === idProductoTalla);
      handleRemoveItem(idProductoTalla, item?.titulo);
      return;
    }
    if (newQuantity > 10) {
      toast.warning('Cantidad máxima: 10 unidades');
      return;
    }
    updateQuantity(idProductoTalla, newQuantity);
  };

  // Form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Helpers
  const formatPrice = (price) => price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  const buildWhatsAppMessage = () => {
    // 1. Cada producto en una sola línea, más visual y compacto.
    const lines = cart.map(item =>
      `✅ ${item.cantidad}x *${item.titulo}* (${item.talla}, ${item.color}) - ${moneda} ${formatPrice(item.precio)} c/u`
    );

    // 2. Se agrupan los detalles clave del pedido.
    const detalles = [
      `👤 *Cliente:* ${formData.name}`,
      `📞 *Contacto:* ${formData.telefono}`,
      formData.deliveryOption === 'delivery'
        ? `🚚 *Entrega:* ${formData.address}`
        : `🛍️ *Recoge en tienda*`,
      formData.paymentMethod === 'efectivo'
        ? `💵 *Pago:* Contra entrega`
        : `💳 *Pago:* Transferencia`
    ];

    // 3. La nota solo se añade si existe.
    if (formData.noteText?.trim()) {
      detalles.push(`📝 *Nota:* ${formData.noteText}`);
    }

    const totalMsg = `${moneda} ${formatPrice(total)}`;

    // 4. Se construye el mensaje final, más limpio y directo.
    const msg =
      `🌟 *¡Nuevo Pedido!* 🌟\n\n` +
      `*Productos:*\n${lines.join('\n')}\n\n` +
      `*Detalles del Pedido:*\n${detalles.join('\n')}\n\n` +
      `*Total a Pagar: ${totalMsg}*`;

    return msg;
  };

  // Crear pedido (JSON → backend normalizado)
  const crearPedido = async () => {
    if (cart.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    // Validaciones
    if (!formData.name.trim()) return toast.error('Ingresa tu nombre');
    if (!formData.identificationNumber.trim()) return toast.error('Ingresa tu identificación');
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) return toast.error('Ingresa un email válido');
    if (!formData.telefono.trim()) return toast.error('Ingresa tu teléfono');
    if (formData.deliveryOption === 'delivery' && !formData.address.trim()) return toast.error('Ingresa tu dirección');

    setIsLoading(true);
    try {
      // items con idProductoTalla + cantidad
      const items = cart.map(item => ({
        idProductoTalla: Number(item.idProductoTalla),
        cantidad: Number(item.cantidad)
      }));

      const payload = {
        estado: 'pendiente',
        items,
        // total (opcional, el servidor recalcula)
        total: Number(total),
        nombre: formData.name,
        numero_identificacion: formData.identificationNumber,
        email: formData.email,
        telefono: formData.telefono,
        entrega: formData.deliveryOption, // 'delivery'|'pickup'
        direccion_entrega: formData.deliveryOption === 'delivery' ? formData.address : null,
        pago: formData.paymentMethod,
        nota: formData.noteText || null,
        // fecha_entrega / codigo → opcionales
      };

      const resp = await fetch(`${baseURL}/pedidoPost.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || 'Error al procesar el pedido');
      }

      const result = await resp.json();

      // Éxito → mostrar datos del pedido y abrir WhatsApp directamente
      await Swal.fire({
        title: '¡Pedido creado exitosamente!',
        html: `
          <div style="text-align:left">
            <p><strong>Número de Pedido:</strong> ${result?.idPedido ?? '-'}</p>
            <p><strong>Código:</strong> ${result?.codigo ?? '-'}</p>
            <p><strong>Total:</strong> ${moneda} ${formatPrice(result?.total_servidor ?? total)}</p>
            <p style="margin-top: 15px;">Tu pedido ha sido registrado correctamente. Ahora serás redirigido a WhatsApp para confirmar los detalles con nuestro vendedor.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Ir a WhatsApp',
        confirmButtonColor: '#25D366'
      });

      // Abrir WhatsApp directamente sin previsualización
      const message = buildWhatsAppMessage();
      const phone = vendorPhone?.replace(/[^0-9]/g, '');
      if (phone) {
        const waUrl = `https://api.whatsapp.com/send?phone=+57${phone}&text=${encodeURIComponent(message)}`;
        window.open(waUrl, '_blank');
      } else {
        toast.error('No se encontró el número del vendedor.');
      }

      // Reset
      clearCart();
      setFormData({
        noteText: '',
        name: '',
        email: '',
        telefono: '',
        identificationNumber: '',
        deliveryOption: 'delivery',
        address: '',
        paymentMethod: 'efectivo'
      });
      handleCloseCheckout();

    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error al procesar el pedido. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Detectar altura real del viewport para móviles
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);

  // Limpiar overflow al desmontar
  useEffect(() => () => { document.body.style.overflow = 'unset'; }, []);

  return (
    <div>

      {/* Botón flotante del carrito */}
      <button onClick={handleOpenCart} className='cartIcon' aria-label="Abrir carrito">
        {totalItems > 0 && (
          <span className="cart-badge">
            {totalItems > 99 ? '99+' : totalItems}
          </span>
        )}
        <FontAwesomeIcon icon={faShoppingCart} />
        <strong>Carrito</strong>
      </button>

      {/* MODAL DEL CARRITO */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseCart}
        className="modal-cart"
        overlayClassName="overlay-cart"
        closeTimeoutMS={200}
        aria={{ labelledby: "cart-title", describedby: "cart-description" }}
      >
        <div className='deFLex'>
          <button onClick={handleCloseCart} aria-label="Cerrar carrito">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h4 id="cart-title">Mi Carrito ({totalItems})</h4>
          <button onClick={handleClearCart} className='deleteToCart' disabled={cart.length === 0} aria-label="Vaciar carrito">
            Vaciar
          </button>
        </div>

        {cart.length === 0 ? (
          <div className='nohay'>
            <p>Tu carrito está vacío</p>
            <small>Agrega productos para comenzar tu compra</small>
          </div>
        ) : (
          <>
            <div className="modal-content-cart" id="cart-description">
              {cart.map(item => (
                <div
                  key={item.idProductoTalla}
                  className={`cardProductCart ${removingItems.has(item.idProductoTalla) ? 'removing' : ''}`}
                >
                  <Anchor
                    to={`/producto/${item.idProducto}/${item.titulo?.replace(/\s+/g, '-')}`}
                    onClick={handleCloseCart}
                    className="product-link"
                  >
                    <img src={item.imagen} alt={item.titulo} loading="lazy" />
                  </Anchor>

                  <div className='cardProductCartText'>
                    <h3 title={item.titulo}>{item.titulo}</h3>
                    <div className='cart-item-details'>
                      <span className="cart-color-line">
                        <span className="variant-label">Color:</span>
                        <span className="cart-color-swatch" style={{ backgroundColor: item.colorCode || '#ccc' }} />
                        {item.color}
                      </span>
                      <span>
                        <span className="variant-label">Talla:</span> {item.talla}
                      </span>
                    </div>
                    <strong>
                      {moneda} {formatPrice(item.precio * item.cantidad)}
                    </strong>
                    <small className="unit-price">
                      {moneda} {formatPrice(item.precio)} c/u
                    </small>
                  </div>

                  <div className='deColumn'>
                    <button
                      onClick={() => handleRemoveItem(item.idProductoTalla, item.titulo)}
                      className='deleteCart'
                      aria-label={`Eliminar ${item.titulo} del carrito`}
                      disabled={removingItems.has(item.idProductoTalla)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>

                    <div className='deFlexCantidad'>
                      <button
                        onClick={() => handleUpdateQuantity(item.idProductoTalla, item.cantidad - 1)}
                        aria-label="Disminuir cantidad"
                        disabled={item.cantidad <= 1}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span className="quantity-display">{item.cantidad}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item.idProductoTalla, item.cantidad + 1)}
                        aria-label="Aumentar cantidad"
                        disabled={item.cantidad >= 10}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className='deColumnCart'>
              <div className="cart-summary">
                <div className="summary-row">
                  <span>Subtotal ({totalItems} productos)</span>
                  <span>{moneda} {formatPrice(total)}</span>
                </div>
                <div className="summary-row total-row">
                  <strong>Total</strong>
                  <strong>{moneda} {formatPrice(total)}</strong>
                </div>
              </div>
              <button
                className='btn btn-primary'
                onClick={handleOpenCheckout}
                disabled={cart.length === 0}
              >
                <FontAwesomeIcon icon={faShoppingBag} />
                Continuar Compra
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL CHECKOUT */}
      <Modal
        isOpen={checkoutModalIsOpen}
        onRequestClose={handleCloseCheckout}
        className="modal-cart checkout-modal"
        overlayClassName="overlay-cart"
        closeTimeoutMS={200}
        aria={{ labelledby: "checkout-title", describedby: "checkout-form" }}
      >
        <div className='deFLex'>
          <button onClick={handleCloseCheckout} aria-label="Volver al carrito">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h4 id="checkout-title">Completa tus datos</h4>
          <div></div>
        </div>

        <div className="modal-send-form" id="checkout-form">
          <div className="form-section">
            <h5>Información personal</h5>
            <input
              type="text"
              name="name"
              placeholder="Nombre completo *"
              value={formData.name}
              onChange={handleInputChange}
              required
              aria-label="Nombre completo"
            />
            <input
              type="text"
              name="identificationNumber"
              placeholder="Número de identificación *"
              value={formData.identificationNumber}
              onChange={handleInputChange}
              required
              aria-label="Número de identificación"
            />
            <input
              type="email"
              name="email"
              placeholder="Email *"
              value={formData.email}
              onChange={handleInputChange}
              required
              aria-label="Email"
            />
            <input
              type="tel"
              name="telefono"
              placeholder="Teléfono *"
              value={formData.telefono}
              onChange={handleInputChange}
              required
              aria-label="Teléfono"
            />
          </div>

          <div className="form-section">
            <h5>Método de entrega</h5>
            <div className='deFLexRadio'>
              <div>
                <input
                  type="radio"
                  id="delivery"
                  name="deliveryOption"
                  value="delivery"
                  checked={formData.deliveryOption === 'delivery'}
                  onChange={handleInputChange}
                />
                <label htmlFor="delivery">Enviar</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="pickup"
                  name="deliveryOption"
                  value="pickup"
                  checked={formData.deliveryOption === 'pickup'}
                  onChange={handleInputChange}
                />
                <label htmlFor="pickup">Recoger</label>
              </div>
            </div>
          </div>

          {formData.deliveryOption === 'delivery' && (
            <div className="form-section">
              <h5>Dirección de entrega</h5>
              <p>El valor depende de la zona de entrega</p>
              <input
                type="text"
                name="address"
                placeholder="Dirección de entrega *"
                value={formData.address}
                onChange={handleInputChange}
                required
                aria-label="Dirección de entrega"
              />
            </div>
          )}

          <div className="form-section">
            <h5>Método de pago</h5>
            <div className='deFLexRadio'>
              <div>
                <input
                  type="radio"
                  id="efectivo"
                  name="paymentMethod"
                  value="efectivo"
                  checked={formData.paymentMethod === 'efectivo'}
                  onChange={handleInputChange}
                />
                <label htmlFor="efectivo">Contra entrega</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="transferencia"
                  name="paymentMethod"
                  value="transferencia"
                  checked={formData.paymentMethod === 'transferencia'}
                  onChange={handleInputChange}
                />
                <label htmlFor="transferencia">Transferencia bancaria</label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h5>Notas adicionales (opcional)</h5>
            <textarea
              name="noteText"
              placeholder="Comentarios sobre tu pedido..."
              value={formData.noteText}
              onChange={handleInputChange}
              rows={3}
              aria-label="Notas adicionales"
            />
          </div>

          <div className="order-summary">
            <h5>Resumen del pedido</h5>
            <div className="summary-items">
              {cart.slice(0, 3).map(item => (
                <div key={item.idProductoTalla} className="summary-item">
                  <span>{item.titulo} x{item.cantidad}</span>
                  <span>{moneda} {formatPrice(item.precio * item.cantidad)}</span>
                </div>
              ))}
              {cart.length > 3 && (
                <div className="summary-item">
                  <span>... y {cart.length - 3} productos más</span>
                </div>
              )}
              <div className="summary-total">
                <strong>
                  <span>Total: {moneda} {formatPrice(total)}</span>
                </strong>
              </div>
            </div>
          </div>
        </div>

        <div className="checkout-footer">
          <button onClick={crearPedido} className='btn btn-primary' disabled={isLoading}>
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Procesando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faHeart} />
                Finalizar Pedido
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}